import type { ComponentProps, ReactNode } from 'react'

import { Slot } from '@radix-ui/react-slot'

import { type VariantProps, cva } from 'class-variance-authority'

import { cn } from '@/lib/utils'

const badgeVariants = cva(
  'inline-flex items-center gap-1 justify-center rounded-xs border px-1 py-[1px] text-xs font-medium w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden',
  {
    variants: {
      variant: {
        default: 'bg-primary border-transparent text-primary-foreground',
        outline: 'text-foreground',
        error: 'bg-destructive border-transparent text-white',
        secondary: 'bg-secondary border-transparent text-secondary-foreground',
        destructive: 'bg-red-100 border-transparent text-destructive',
        destructive1:
          'bg-destructive border-transparent text-primary-foreground',
        destructive2: 'bg-red-700 border-transparent text-white',
        warning: 'bg-orange-100 border-1 border-transparent text-orange-600',
        warning2: 'bg-white border-orange-300 text-orange-600',
        warning3: 'bg-orange-500 border-transparent text-white',
        warning4: 'bg-amber-50 border-transparent text-amber-600',
        warning5: 'bg-orange-50 border-orange-200 text-orange-700',
        success: 'bg-white border-green-400 text-green-700',
        success1: 'bg-green-50 border-1 border-transparent text-green-700',
        success2: 'bg-green-50 border-1 border-transparent text-green-600',
        success3: 'bg-green-100 border-transparent text-green-800',
        info: 'bg-white border-blue-300 text-blue-600',
        info2: 'bg-blue-100 border-blue-600 text-blue-600',
        // Like info2 but white fill instead of light-blue — used by the
        // waiting step-phase badges (Queued, Preparing, Awaiting Start/Cleanup).
        info2White: 'bg-white border-blue-600 text-blue-600',
        info3: 'bg-blue-50 border-1 border-transparent text-blue-600',
        info4: 'bg-sky-50 border-1 border-transparent text-sky-600',
        info5: 'bg-cyan-50 border-1 border-transparent text-black',
        info6:
          'bg-blue-600 border-1 border-transparent text-primary-foreground',
        info7: 'bg-blue-100 border-1 border-blue-600 text-blue-600',
        info8: 'bg-blue-50 border-transparent text-sky-600',
        info9: 'bg-cyan-50 border-transparent text-cyan-700',
        edit: 'bg-white border-yellow-500 text-yellow-700',
        neutral: 'bg-gray-100 border-1 border-transparent text-foreground',
        neutral1: 'bg-gray-100 border-1 border-transparent text-foreground',
        neutral2: 'bg-gray-50 border-1 border-gray-600 text-gray-600',
        neutral3: 'bg-gray-50 border-1 border-transparent text-gray-600',
        neutral4: 'bg-gray-50 border-1 border-gray-600 text-gray-600',
        neutral5: 'bg-gray-500 border-1 border-gray-300 text-gray-50',
        neutral6: 'bg-gray-200 border-1 border-gray-600 text-gray-600',
        neutral7: 'bg-gray-900 border-transparent text-gray-50',
        neutral8: 'bg-slate-600 border-transparent text-primary-foreground',
        parameter: 'bg-purple-50 border-1 border-transparent text-purple-700',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
)

export type Props = ComponentProps<'span'> &
  VariantProps<typeof badgeVariants> & {
    asChild?: boolean
    icon?: ReactNode
  }

function Badge({
  className,
  variant,
  asChild = false,
  children,
  icon,
  ...props
}: Props) {
  const Component = asChild ? Slot : 'span'

  return (
    <Component
      data-slot="badge"
      className={cn(badgeVariants({ variant }), className)}
      {...props}
    >
      <>
        {icon}
        {children}
      </>
    </Component>
  )
}

export { Badge, badgeVariants }
